// File: frontend/src/components/ui/StatusBadge.js
//
// Shared semantic status pill, generalised from the Vouchers page's StatusBadge.
// Colours are derived from the MUI theme palette (success/info/warning/error +
// neutral text), so the badge is automatically correct in BOTH light and dark
// mode with no per-page dark/light branching.

import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";

// Maps a domain status → a semantic tone + default label. Callers may override
// the label. Unknown statuses fall back to a neutral pill using the raw value.
const STATUS_MAP = {
  online:    { tone: "success", label: "Online" },
  active:    { tone: "success", label: "Active" },
  available: { tone: "success", label: "Available" },
  unused:    { tone: "success", label: "Available" },
  completed: { tone: "success", label: "Completed" },
  paid:      { tone: "success", label: "Paid" },
  success:   { tone: "success", label: "Success" },

  redeemed:  { tone: "info",    label: "Redeemed" },
  used:      { tone: "info",    label: "Redeemed" },
  info:      { tone: "info",    label: "Info" },

  checking:  { tone: "warning", label: "Checking" },
  pending:   { tone: "warning", label: "Pending" },
  processing:{ tone: "warning", label: "Processing" },
  // Explicit amber tone for anomalies that are neither healthy nor failed —
  // e.g. a customer who is ONLINE while their recorded entitlement reads
  // expired. Without this key such callers fell through to the neutral pill,
  // which read as ordinary and hid the exception.
  warning:   { tone: "warning", label: "Warning" },

  expired:   { tone: "error",   label: "Expired" },
  failed:    { tone: "error",   label: "Failed" },
  error:     { tone: "error",   label: "Error" },

  offline:   { tone: "neutral", label: "Offline" },
  inactive:  { tone: "neutral", label: "Inactive" },
  unknown:   { tone: "neutral", label: "Unknown" },
};

const SIZES = {
  sm: { px: 0.75, py: 0.2, font: 10.5, dot: 5 },
  md: { px: 1,    py: 0.3, font: 11,   dot: 5 },
  lg: { px: 1.25, py: 0.4, font: 12,   dot: 6 },
};

export default function StatusBadge({ status, label, dot = true, size = "md", sx }) {
  const theme = useTheme();
  const key = String(status ?? "").toLowerCase();
  const cfg = STATUS_MAP[key] || { tone: "neutral", label: status || "—" };
  const s = SIZES[size] || SIZES.md;

  const color = cfg.tone === "neutral"
    ? theme.palette.text.secondary
    : theme.palette[cfg.tone].main;
  // The label is 10.5–12px text on a 10% tint of `color`. In light mode `main`
  // is a fill colour (amber measures ~2.9:1 there), so the words use the tone's
  // text shade; dark mode's `text` is its `main`.
  const textColor = cfg.tone === "neutral"
    ? color
    : theme.palette[cfg.tone].text || color;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5,
        px: s.px, py: s.py, borderRadius: 99,
        bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.14 : 0.1),
        border: `1px solid ${alpha(color, 0.28)}`,
        whiteSpace: "nowrap", flexShrink: 0,
        ...sx,
      }}
    >
      {dot && <Box sx={{ width: s.dot, height: s.dot, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />}
      <Typography component="span" sx={{ fontSize: s.font, fontWeight: 700, color: textColor, lineHeight: 1.4 }}>
        {label || cfg.label}
      </Typography>
    </Box>
  );
}

export { STATUS_MAP };
